/**
 * ReviewGuide: the visual guide of a question's page and its review (/questions/:uuid), opened by the "?" of the
 * page's top bar (QuestionTopBar: after the Question | Versions tabs, so both tabs have it). Texts under
 * help.review.*; the buttons, tabs and sections are named through their own i18n keys.
 * - Steps follow the review as the code runs it (questions/utils/review.py on the server): the status, the like or
 *   dislike of the question card's VoteBar (with its comment, and the note of a question of the reader's review
 *   task), the decision (the detail's actions.transitions; a comment for a return or a refusal in a subject, and for
 *   an approval, which also asks to tick its four checks: DecisionPanel), the author's resubmit, the history (status
 *   changes and votes), the versions and their comparison, the edit and the delete of the top bar, and the lock of a
 *   question an active BTS booklet prints.
 * - It follows the reader: the vote step for a subject's experts, moderators and admins (canReviewIn; its task note
 *   for experts and moderators, who get review tasks); the decision step and the queue tip for those who decide in a
 *   subject, its moderators and admins (canDecideIn: since 2026-10-29 its experts only vote), and the decision step
 *   for the old controllers outside folders (no "take", a comment needed only to approve); the resubmit for those who
 *   write questions (experts too), the delete for those who may delete; the top bar is drawn as it is on phones under
 *   768px (Edit in a row of its own next to "⋯"); admins, who open the BTS pages, are told the lock notice's booklet
 *   is a link to its page, whose "⋯" menu returns it to draft.
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ArchiveBoxIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  BookOpenIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  KeyIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
// A vote in the history: the solid thumb, as StatusHistory draws it.
import { HandThumbUpIcon as VotedUpIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpLegend, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDecision,
  MiniField,
  MiniLevel,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTabs,
  MiniText,
} from "../Mini";
import { QuestionStatusBadge, STATUS_ORDER } from "../../QuestionList/questionStatus";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canCreateIn, canDecideIn, canReviewIn, isAdminUser } from "../../../utils/folders";

// What an approval asks to tick first (DecisionPanel's APPROVE_CHECKS, in its order).
const APPROVE_CHECKS = ["text", "answer", "topic", "level"];

// The lock notice's booklet link (bookletLock.js BookletLinks): the name, bold and underlined, then ↗. Mini has no
// inline link yet (a candidate for Mini.js): this one keeps Mini's text and icon sizes, and `mark` works as on the
// Mini controls.
function MiniInlineLink({ children, mark }) {
  const link = (
    <span className="inline-flex min-w-0 max-w-full items-center gap-0.5 whitespace-nowrap px-0.5 align-middle text-[11px] font-semibold text-blue-900 underline underline-offset-2 md:text-xs">
      <span className="truncate">{children}</span>
      <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0 md:h-3.5 md:w-3.5" />
    </span>
  );
  if (!mark) return link;
  return (
    <Mark round="md" {...(mark === true ? { pointer: true } : typeof mark === "object" ? mark : { pointer: true, label: mark })}>
      {link}
    </Mark>
  );
}

export default function ReviewGuide() {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The top bar's own breakpoint: one row from md up; on phones Edit and "⋯" share a row of their own.
  const wide = useMediaQuery("(min-width: 768px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const rx = (key) => t(`help.review.example.${key}`);

  const isAdmin = isAdminUser(user);
  const roles = user?.folder_roles || [];
  const perms = user?.perms || [];
  // Who votes in a subject (its experts and moderators, admins: they like or dislike others' questions there), and
  // who decides there (its moderators and admins; its experts only vote); outside folders the controllers decide
  // only (no "take", a comment needed only to approve). Who may get a review task (a subject's experts and
  // moderators: the vote bar names it). Who writes questions (experts too; and so may get one back for changes), who
  // may delete one (moderators any, authors and experts their own early on).
  const votesInSubjects = isAdmin || roles.some(canReviewIn);
  const inSubjects = isAdmin || roles.some(canDecideIn);
  const reviews = inSubjects || perms.includes("approve_question");
  const getsTasks = roles.includes("expert") || roles.includes("moderator");
  const writes = isAdmin || roles.some(canCreateIn) || perms.includes("create_question");
  const edits = reviews || writes;
  const deletes =
    isAdmin || roles.includes("moderator") || roles.includes("author") || roles.includes("expert") || perms.includes("create_question");
  // Only admins open the BTS pages: their lock notice links the booklets.
  const opensBts = isAdmin;

  const step = (key) => t(`question_detail.transitions.${key}`);
  const take = step("under_review");
  const approve = step("approved");
  const giveBack = step("changes_requested");
  const decline = step("declined");
  const panel = t("question_detail.decision.title");
  // The decision panel's steps in the server's order, in a subject or outside folders.
  const choices = inSubjects
    ? [
        ["UNDER_REVIEW", take],
        ["APPROVED", approve],
        ["CHANGES_REQUESTED", giveBack],
        ["DECLINED", decline],
      ]
    : [
        ["APPROVED", approve],
        ["DECLINED", decline],
        ["CHANGES_REQUESTED", giveBack],
      ];
  const voteBlock = t("question_detail.vote_bar.title");
  const like = t("question_detail.vote_bar.like");
  const dislike = t("question_detail.vote_bar.dislike");
  const commentOpen = t("question_detail.vote_bar.comment_open");
  const history = t("question_detail.history.title");
  const edit = t("buttons.edit");
  const remove = t("buttons.delete");
  const resubmit = step("resubmit");
  const versionsTab = t("question_versions.tabs.versions");
  const compare = t("question_versions.compare");
  const tabs = [
    { label: t("question_versions.tabs.question"), icon: DocumentTextIcon },
    { label: versionsTab, icon: ClockIcon },
  ];
  const questionTitle = t("question_detail.question_id", { code: rx("code") });
  const historyRecord = (code, { comment = false, mark } = {}) => (
    <MiniCard mark={mark}>
      <MiniRow>
        <MiniText variant="strong">{rx("reviewer")}</MiniText>
        <MiniText variant="muted">({t("question_detail.roles.approver")})</MiniText>
        <MiniStatus code={code} />
      </MiniRow>
      {comment && <MiniText>{rx("comment")}</MiniText>}
    </MiniCard>
  );
  // The notice as the page words it for one booklet (an admin's names it by its link).
  const booklet = rx("booklet");
  const lockText = opensBts
    ? `${t("question_detail.booklet_lock.notice_linked", { count: 1 })} ${t("question_detail.booklet_lock.return", { count: 1 })}`
    : `${t("question_detail.booklet_lock.notice", { names: booklet, count: 1 })} ${t("question_detail.booklet_lock.ask_admin", { count: 1 })}`;
  // The booklet page's own buttons: quiet from md up, framed on phones (BookletStatusActions).
  const bookletButton = wide ? "quiet" : "outline";

  return (
    <>
      <HelpIntro>{t("help.review.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="review" />

      <HelpSteps>
        <HelpStep
          title={t("help.review.steps.status.title")}
          illustration={
            <MiniCard>
              <MiniRow className="justify-between gap-y-3">
                <MiniText variant="title">{questionTitle}</MiniText>
                {/* The card's caption above the status pill, as on the page. */}
                <MiniStack tight>
                  <MiniRow>
                    <MiniText variant="caps">{t("question_detail.status")}</MiniText>
                  </MiniRow>
                  <MiniRow>
                    <MiniStatus code="UNDER_REVIEW" mark={{ pointer: false }} />
                  </MiniRow>
                </MiniStack>
              </MiniRow>
              <MiniRow>
                <MiniBadge tone="blue" icon={BookOpenIcon}>
                  {ex("subject")}
                </MiniBadge>
                <MiniLevel level="B" />
              </MiniRow>
            </MiniCard>
          }
        >
          <HelpText i18nKey="help.review.steps.status.text" values={{ status: t("question_detail.status") }} />
        </HelpStep>

        {/* The question card's vote bar: the like (1) and its comment (2); above them, the note of a question of the
            reader's review task. */}
        {votesInSubjects && (
          <HelpStep
            title={t("help.review.steps.vote.title")}
            illustration={
              <MiniCard>
                {getsTasks && (
                  <MiniCard tone="blue" icon={ClipboardDocumentCheckIcon}>
                    {t("question_detail.vote_bar.task_todo", { task: t("folders.tasks.review_title", { count: 2 }) })}
                  </MiniCard>
                )}
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="strong">{voteBlock}</MiniText>
                  <MiniRow>
                    {/* No cursor on "1": it would cover the hint right under it. */}
                    <MiniButton icon={HandThumbUpIcon} mark={{ label: "1" }}>
                      {like}
                    </MiniButton>
                    <MiniButton icon={HandThumbDownIcon}>{dislike}</MiniButton>
                  </MiniRow>
                </MiniRow>
                <MiniText variant="muted">{t("question_detail.vote_bar.hint")}</MiniText>
                <MiniRow>
                  <MiniButton variant="link" icon={ChatBubbleLeftEllipsisIcon} mark="2">
                    {commentOpen}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
            }
          >
            <HelpText i18nKey="help.review.steps.vote.text" values={{ block: voteBlock, like, dislike }} />
            <p>
              <HelpText i18nKey="help.review.steps.vote.text_comment" values={{ comment: commentOpen }} />
            </p>
            {getsTasks && (
              <p>
                <HelpText i18nKey="help.review.steps.vote.text_task" values={{ tasks: t("nav.my_tasks") }} />
              </p>
            )}
          </HelpStep>
        )}

        {/* One step: the approval (1), its four checks and its reason, then the confirmation that names it (2). */}
        {reviews && (
          <HelpStep
            title={t("help.review.steps.decide.title")}
            illustration={
              <MiniScreen title={panel}>
                <MiniText variant="muted">{t("question_detail.decision.select")}</MiniText>
                <MiniRow className="gap-y-3">
                  {choices.map(([code, label]) => (
                    <MiniDecision key={code} code={code} selected={code === "APPROVED"} mark={code === "APPROVED" ? "1" : undefined}>
                      {label}
                    </MiniDecision>
                  ))}
                </MiniRow>
                <MiniCard tone="green">
                  <MiniText variant="strong">{t("question_detail.review.approve.checks_title")}</MiniText>
                  {APPROVE_CHECKS.map((key) => (
                    <MiniRow key={key} className="flex-nowrap">
                      <MiniCheck checked />
                      <MiniText>{t(`question_detail.review.approve.check_${key}`)}</MiniText>
                    </MiniRow>
                  ))}
                </MiniCard>
                <MiniRow>
                  <MiniField label={t("question_detail.review.approve.reason_label")} value={rx("approve_reason")} className="flex-1" />
                </MiniRow>
                <MiniRow className="justify-end">
                  <MiniButton variant="primary" mark="2">
                    {t("question_detail.review.confirm.approved")}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
            }
          >
            <HelpText
              i18nKey={inSubjects ? "help.review.steps.decide.text" : "help.review.steps.decide.text_outside"}
              values={{ panel, take, approve, return: giveBack, decline }}
            />
            <p>
              <HelpText
                i18nKey={inSubjects ? "help.review.steps.decide.text_confirm" : "help.review.steps.decide.text_confirm_outside"}
                values={{ approve, return: giveBack, decline }}
              />
            </p>
          </HelpStep>
        )}

        {writes && (
          <HelpStep
            title={t("help.review.steps.resubmit.title")}
            illustration={
              <MiniStack>
                <MiniRow className={wide ? "justify-end" : ""}>
                  <MiniButton variant="primary" icon={PencilSquareIcon} mark="1">
                    {edit}
                  </MiniButton>
                </MiniRow>
                <MiniScreen title={t("question_detail.review.feedback.changes_requested_title")}>
                  <MiniCard tone="slate">
                    <MiniRow>
                      <MiniText variant="strong">{t("question_detail.review.feedback.reviewer_comment")}</MiniText>
                    </MiniRow>
                    <MiniText>{rx("comment")}</MiniText>
                  </MiniCard>
                  <MiniRow className="justify-end">
                    <MiniButton variant="primary" icon={PaperAirplaneIcon} mark="2">
                      {resubmit}
                    </MiniButton>
                  </MiniRow>
                </MiniScreen>
              </MiniStack>
            }
          >
            <HelpText i18nKey="help.review.steps.resubmit.text" values={{ edit, resubmit }} />
          </HelpStep>
        )}

        <HelpStep
          title={t("help.review.steps.history.title")}
          illustration={
            // Newest first: a return with its comment, then an expert's like with theirs (StatusHistory's records).
            <MiniScreen title={history}>
              {historyRecord("CHANGES_REQUESTED", { comment: true })}
              <MiniCard mark={{ pointer: false }}>
                <MiniRow>
                  <MiniText variant="strong">{rx("expert")}</MiniText>
                  <MiniText variant="muted">({t("question_detail.roles.checker")})</MiniText>
                  <MiniBadge tone="green" icon={VotedUpIcon}>
                    {t("question_detail.history.upvoted")}
                  </MiniBadge>
                </MiniRow>
                <MiniText>{rx("vote_comment")}</MiniText>
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.review.steps.history.text" values={{ history }} />
        </HelpStep>

        <HelpStep
          title={t("help.review.steps.versions.title")}
          illustration={
            // The tabs are the top bar's; the list is the tab's card.
            <MiniStack>
              <MiniRow>
                <MiniTabs items={tabs} current={1} />
              </MiniRow>
              <MiniScreen title={t("question_versions.title")}>
                <MiniCard icon={<MiniCheck checked />}>
                  <MiniRow>
                    <MiniText variant="strong">{t("question_versions.version", { number: 3 })}</MiniText>
                    <MiniBadge tone="blue">{t("question_versions.latest")}</MiniBadge>
                  </MiniRow>
                </MiniCard>
                <MiniCard icon={<MiniCheck checked />}>
                  <MiniText variant="strong">{t("question_versions.version", { number: 2 })}</MiniText>
                </MiniCard>
                <MiniCard icon={<MiniCheck />}>
                  <MiniText variant="strong">{t("question_versions.version", { number: 1 })}</MiniText>
                </MiniCard>
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="muted">{t("question_versions.select_ready", { from: 2, to: 3 })}</MiniText>
                  <MiniButton variant="primary" icon={ArrowsRightLeftIcon} mark>
                    {compare}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.review.steps.versions.text" values={{ tab: versionsTab, compare }} />
        </HelpStep>

        {edits && (
          <HelpStep
            title={t(deletes ? "help.review.steps.actions.title" : "help.review.steps.actions.title_edit")}
            illustration={
              <MiniScreen title={questionTitle}>
                {/* Back, the tabs and the guide's "?", then Edit and "⋯": one row from md up, two on phones. */}
                {wide ? (
                  <MiniRow className="justify-between gap-y-3">
                    <MiniRow>
                      <MiniButton variant="quiet" icon={ArrowLeftIcon}>
                        {t("buttons.back")}
                      </MiniButton>
                      <MiniTabs items={tabs} current={0} />
                      <MiniButton variant="quiet" icon={QuestionMarkCircleIcon} />
                    </MiniRow>
                    <MiniRow>
                      <MiniButton variant="primary" icon={PencilSquareIcon} mark={deletes ? "1" : true}>
                        {edit}
                      </MiniButton>
                      {deletes && <MiniButton variant="quiet" icon={EllipsisHorizontalIcon} />}
                    </MiniRow>
                  </MiniRow>
                ) : (
                  <>
                    <MiniRow className="flex-nowrap">
                      <MiniButton variant="quiet" icon={ArrowLeftIcon} />
                      <MiniTabs items={tabs} current={0} />
                      <MiniButton variant="quiet" icon={QuestionMarkCircleIcon} />
                    </MiniRow>
                    <MiniRow className="flex-nowrap">
                      <MiniButton variant="primary" icon={PencilSquareIcon} mark={deletes ? "1" : true}>
                        {edit}
                      </MiniButton>
                      {deletes && <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />}
                    </MiniRow>
                  </>
                )}
                {deletes && (
                  <MiniRow className="justify-end">
                    <MiniMenu items={[{ label: remove, icon: TrashIcon, danger: true, mark: "2" }]} />
                  </MiniRow>
                )}
              </MiniScreen>
            }
          >
            <HelpText i18nKey="help.review.steps.actions.text" values={{ edit }} />
            {deletes && (
              <p>
                <HelpText i18nKey="help.review.steps.actions.text_delete" values={{ delete: remove }} />
              </p>
            )}
          </HelpStep>
        )}

        <HelpStep
          title={t("help.review.steps.lock.title")}
          illustration={
            opensBts ? (
              // An admin's notice links the booklet (1); on its page, "⋯" holds the way back to draft (2).
              <MiniStack>
                {/* The link on a line of its own, so that its marker keeps clear of the text. */}
                <MiniCard tone="blue" icon={LockClosedIcon}>
                  <MiniText>{lockText}</MiniText>
                  <MiniRow className="gap-y-3">
                    <MiniInlineLink mark="1">{booklet}</MiniInlineLink>
                  </MiniRow>
                </MiniCard>
                <MiniScreen title={booklet}>
                  <MiniRow className="justify-end">
                    <MiniButton variant={bookletButton} icon={ArchiveBoxIcon}>
                      {t("bts.actions.archive")}
                    </MiniButton>
                    <MiniButton variant={bookletButton} icon={EllipsisHorizontalIcon} />
                  </MiniRow>
                  <MiniRow className="justify-end">
                    <MiniMenu items={[{ label: t("bts.actions.to_draft"), icon: ArrowUturnLeftIcon, mark: "2" }]} />
                  </MiniRow>
                </MiniScreen>
              </MiniStack>
            ) : (
              <MiniCard tone="blue" icon={LockClosedIcon} mark={{ pointer: false }}>
                <MiniText>{lockText}</MiniText>
              </MiniCard>
            )
          }
        >
          {t("help.review.steps.lock.text")}
          {opensBts && (
            <p>
              <HelpText i18nKey="help.review.steps.lock.text_admin" values={{ to_draft: t("bts.actions.to_draft") }} />
            </p>
          )}
        </HelpStep>
      </HelpSteps>

      <HelpLegend
        rows={STATUS_ORDER.map((code) => ({
          key: code,
          badge: <QuestionStatusBadge status={{ code }} />,
          text: t(`help.review.legend.${code.toLowerCase()}`),
        }))}
      />

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.review.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.review.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.review.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.review.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.review.roles.viewer") },
        ]}
      />

      <HelpTips
        items={[
          t("help.review.tips.allowed"),
          // Returning to the queue is the step of those who decide in a subject (outside folders there is no queue).
          inSubjects && (
            <HelpText key="queue" i18nKey="help.review.tips.queue" values={{ queue: t("question_detail.transitions.pending_review_return") }} />
          ),
          <HelpText key="versions" i18nKey="help.review.tips.versions" values={{ history }} />,
          <HelpText key="latest" i18nKey="help.review.tips.latest" values={{ latest: t("question_versions.latest_changes") }} />,
        ]}
      />
    </>
  );
}
